import { useCallback, useMemo } from "react";
import GameMpProvider from "@/core/data-provider/data-mp/game/game-mp-provider";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { OwnerType } from "@/domains/inventory/constants/ownerType";
import { inventoryByIdSelector, setMemoizedItem } from "@/domains/inventory/slice";
import getDisabledItemsByFilters from "@/domains/inventory/helpers/getDisabledItemsByFilters";
import useInventoryManager from "@/domains/inventory/hooks/inventory/useInventoryManager";
import useContextMenu from "@/domains/inventory/hooks/useContextMenu";
import useFilterItems from "@/domains/inventory/hooks/useFilterItems";
import useSearchItems from "@/domains/inventory/hooks/useSearchItems";
import InventoryCommonHeader from "@/domains/inventory/components/InventoryCommonHeader";
import InventoryContent from "@/domains/inventory/components/InventoryContent";
import InventoryFilter from "@/domains/inventory/components/InventoryFilter";
import InventoryFooter from "@/domains/inventory/components/InventoryFooter";
import InventoryHeader from "@/domains/inventory/components/InventoryHeader";
import InventorySearch from "@/domains/inventory/components/InventorySearch";
import InventoryWeight from "@/domains/inventory/components/InventoryWeight";
import InventoryWrapper from "@/domains/inventory/components/InventoryWrapper";
import InventoryContextMenu from "@/domains/inventory/components/contextMenu/InventoryContextMenu";
import InventoryGrid from "@/domains/inventory/components/dndkit/InventoryGrid";

type Props = { inventoryId: number };

export default function InventoryCharacterTrunkContainer({ inventoryId }: Props) {
  const { handleContextMenu, isOpen, menuRef, position, itemForContextMenu, closeContextMenu } =
    useContextMenu();
  const dispatch = useAppDispatch();
  const inventory = useAppSelector((state) => inventoryByIdSelector(state, inventoryId));
  const { filteredItems, filterGroups, selectedGroups, onSelectHandler } = useFilterItems(
    inventory?.items || [],
  );
  const { onChangeSearchValue, deferredSearchValue, itemsBySearch } = useSearchItems(filteredItems);

  const {
    moveItem,
    equipItem,
    stackItems,
    attachItem,
    applyItem,
    detachItem,
    dropItem,
    splitItem,
  } = useInventoryManager();

  const disabledItems = useMemo(() => {
    if (inventory) {
      return getDisabledItemsByFilters(inventory.items, itemsBySearch);
    }
    return [];
  }, [inventory, itemsBySearch]);

  const onCloseHandler = useCallback(() => {
    GameMpProvider.closeCurrentInterface();
  }, []);

  if (!inventory) return null;

  return (
    <InventoryWrapper className="max-h-[31.875rem]">
      <InventoryHeader className="px-0 border border-b-0 border-dark-900 rounded-t-[inherit] overflow-clip">
        <InventoryCommonHeader
          onClose={onCloseHandler}
          ownerType={OwnerType.VehicleTrunk}
          iconClassName="text-white/50"
        />
      </InventoryHeader>
      <InventoryContent className="flex items-center justify-between w-full h-[3rem]">
        <InventorySearch
          value={deferredSearchValue}
          onChange={onChangeSearchValue}
          className="w-fit shrink"
        />
        {filterGroups.length > 0 && (
          <InventoryFilter
            groups={filterGroups}
            selectedGroups={selectedGroups}
            onSelect={onSelectHandler}
          />
        )}
      </InventoryContent>
      <InventoryContent className="flex-1 overflow-clip overflow-y-auto pb-5">
        <InventoryGrid
          inventoryId={inventory.id}
          cols={inventory.size.width}
          rows={inventory.size.height}
          disabledItems={disabledItems}
          items={inventory.items}
          onMove={moveItem}
          onStack={stackItems}
          onAttach={attachItem}
          onMemoizedItem={(item) => dispatch(setMemoizedItem(item))}
          onContextMenu={handleContextMenu}
        />
        {itemForContextMenu && isOpen && (
          <InventoryContextMenu
            ref={menuRef}
            position={position}
            item={itemForContextMenu}
            onApply={applyItem}
            onDetach={detachItem}
            onDrop={dropItem}
            onEquip={equipItem}
            onSplit={splitItem}
            onClose={closeContextMenu}
          />
        )}
      </InventoryContent>

      <InventoryFooter>
        <InventoryWeight weight={inventory.weight} capacityWeight={inventory.maxWeight} />
      </InventoryFooter>
    </InventoryWrapper>
  );
}
